
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { UserProfile } from '@/types/user';
import { CommunityMember } from '@/types/community';

export interface ExtendedCommunityMember extends CommunityMember {
  user_profile: UserProfile | null;
}

export const COMMUNITY_MEMBERS_QUERY_KEY = 'community-members';

export function useUserProfiles(communityId: string, searchTerm?: string, currentPage?: number, pageSize?: number) {
  const { data: response, isLoading, error, refetch } = useQuery({
    queryKey: [COMMUNITY_MEMBERS_QUERY_KEY, communityId, searchTerm, currentPage, pageSize],
    queryFn: async () => {
      if (!communityId) {
        return { members: [], totalCount: 0, totalPages: 0 };
      }

      let query = supabase
        .from('community_members')
        .select(`
          id,
          community_id,
          user_id,
          role_name,
          joined_at,
          username,
          status,
          user_profiles(*)
        `, { count: 'exact' })
        .eq('community_id', communityId)
        .not('user_profiles', 'is', null)
        .not('user_profiles.is_superadmin', 'eq', true);

      // Apply search filter
      if (searchTerm) {
        query = query.or(`user_profiles.firstname.ilike.%${searchTerm}%,user_profiles.lastname.ilike.%${searchTerm}%,user_profiles.email.ilike.%${searchTerm}%,user_profiles.town.ilike.%${searchTerm}%,user_profiles.country.ilike.%${searchTerm}%,role_name.ilike.%${searchTerm}%,status.ilike.%${searchTerm}%`);
      }

      // Apply pagination if provided
      if (currentPage && pageSize) {
        const from = (currentPage - 1) * pageSize;
        const to = from + pageSize - 1;
        query = query.range(from, to);
      }

      query = query
        .order('joined_at', { ascending: false }); // Most recent first

      const { data, error, count } = await query;

      if (error) {
        console.error('Error fetching user profiles:', error);
        throw error;
      }

      const transformedData = data?.map(member => {
        return {
          ...member,
          user_profile: member.user_profiles || null
        } as ExtendedCommunityMember;
      }) || [];

      return {
        members: transformedData,
        totalCount: count || 0,
        totalPages: pageSize ? Math.ceil((count || 0) / pageSize) : 1
      };
    },
    enabled: !!communityId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 3
  });

  return {
    members: response?.members || [],
    totalCount: response?.totalCount || 0,
    totalPages: response?.totalPages || 0,
    isLoading,
    error,
    refetch
  };
}
